import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchProceduresList,
  trashProcedure,
  editProcedure,
  updateProcedure,
  fetchSpecialtiesList,
} from "../../Adapters/ApiAdapter";
import { baseImage_path, baseUrl } from "../../variables/Variables";
import { useNavigate } from "react-router-dom";
import CKEditorField from "../../components/CKEditorField";

const ProcedureList = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteProcedureId, setDeleteProcedureId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);
  const [editProcedureItem, setEditProcedureItem] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [imgFile, setImgFile] = useState(null);
  const [bannerImgFile, setBannerImgFile] = useState(null);
  const [ogImgFile, setOgImgFile] = useState(null);
  const [twitterImgFile, setTwitterImgFile] = useState(null);

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const data = await fetchProceduresList();
      if (data && data.procedures) {
        setProcedures(data.procedures);
      }
    } catch (error) {
      toast.error("Failed to fetch procedures.");
    } finally {
      setLoading(false);
    }
  };

  const loadSpecialties = async () => {
    try {
      const response = await fetchSpecialtiesList();
      if (response && response.specialities) {
        setSpecialties(response.specialities);
      }
    } catch (error) {
      toast.error("Failed to load specialties.");
    }
  };

  useEffect(() => {
    fetchData();
    loadSpecialties();
  }, []);

  const handleEdit = async (id) => {
    setLoadingAction(true);
    try {
      const response = await editProcedure(id);
      if (response && response.data) {
        setEditProcedureItem(response.data);
      }
    } catch (error) {
      toast.error("Failed to load procedure details.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);
    const data = new FormData();
    Object.keys(editProcedureItem).forEach((key) => {
      if (editProcedureItem[key] !== null && editProcedureItem[key] !== undefined) {
        data.append(key, editProcedureItem[key]);
      }
    });

    if (imgFile) data.append("img_path", imgFile);
    if (bannerImgFile) data.append("banner_img", bannerImgFile);
    if (ogImgFile) data.append("og_image", ogImgFile);
    if (twitterImgFile) data.append("twitter_image", twitterImgFile);

    try {
      const response = await updateProcedure(editProcedureItem.id, data);
      if (response.status === "true") {
        toast.success("Procedure updated successfully!");
        fetchData();
        setEditProcedureItem(null);
        setImgFile(null);
        setBannerImgFile(null);
        setOgImgFile(null);
        setTwitterImgFile(null);
      } else {
        toast.error(response.message || "Failed to update procedure.");
      }
    } catch (error) {
      toast.error("Error updating procedure.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProcedureId) return;
    setLoadingAction(true);
    try {
      const response = await trashProcedure(deleteProcedureId);
      if (response.status === "true") {
        toast.success("Procedure deleted successfully!");
        setProcedures(procedures.filter((p) => p.id !== deleteProcedureId));
      } else {
        toast.error(response.message || "Failed to delete procedure.");
      }
    } catch (error) {
      toast.error("Error deleting procedure.");
    } finally {
      setLoadingAction(false);
      setDeleteProcedureId(null);
    }
  };

  const filteredProcedures = procedures.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.speciality_name && p.speciality_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "img_path",
      headerName: "Thumbnail",
      width: 100,
      renderCell: (params) => (
        <img
          src={params.value ? `${baseImage_path}/${params.value.replace(/^\.\//, "")}` : "https://via.placeholder.com/50"}
          alt={params.row.name}
          style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
        />
      ),
    },
    { field: "name", headerName: "Procedure Name", flex: 1 },
    { field: "speciality_name", headerName: "Speciality", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value === "active" ? "green" : "red",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        >
          {params.value === "active" ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => setDeleteProcedureId(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom>
          Procedures List
        </Typography>
        <Button
          variant="contained"
          color="success"
          onClick={() => navigate("/add-procedure")}
          sx={{ background: "#0ca84f" }}
        >
          Add New Procedure
        </Button>
      </Box>

      <Box mt="20px" display="flex" gap="10px">
        <TextField
          label="Search Procedures"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <Box mt="20px" sx={{ height: 600, width: "100%" }}>
        {loading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredProcedures}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
          />
        )}
      </Box>

      {/* Edit Modal */}
      {editProcedureItem && (
        <Dialog open onClose={() => setEditProcedureItem(null)} fullWidth maxWidth="md">
          <DialogTitle>Edit Procedure: {editProcedureItem.name}</DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              margin="normal"
              label="Procedure Name"
              value={editProcedureItem.name}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, name: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Slug"
              value={editProcedureItem.slug}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, slug: e.target.value })}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Mapped Speciality</InputLabel>
              <Select
                value={editProcedureItem.speciality_id}
                onChange={(e) => setEditProcedureItem({ ...editProcedureItem, speciality_id: e.target.value })}
                label="Mapped Speciality"
              >
                {specialties.map((spec) => (
                  <MenuItem key={spec.id} value={spec.id}>
                    {spec.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={editProcedureItem.status}
                onChange={(e) => setEditProcedureItem({ ...editProcedureItem, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Display Order"
              type="number"
              value={editProcedureItem.display_order}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, display_order: e.target.value })}
            />

            <Typography variant="body1" mt={2}>Overview Content</Typography>
            <CKEditorField
              value={editProcedureItem.overview}
              onChange={(data) => setEditProcedureItem({ ...editProcedureItem, overview: data })}
              uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
            />

            <TextField
              fullWidth
              margin="normal"
              label="SEO Title"
              value={editProcedureItem.seo_title}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, seo_title: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Meta Title"
              value={editProcedureItem.meta_title}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, meta_title: e.target.value })}
            />
            <Typography variant="body1" mt={2}>Meta Description</Typography>
            <CKEditorField
              value={editProcedureItem.meta_description}
              onChange={(data) => setEditProcedureItem({ ...editProcedureItem, meta_description: data })}
              uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Meta Keywords"
              value={editProcedureItem.meta_keywords}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, meta_keywords: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Canonical Link"
              value={editProcedureItem.canonical_link}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, canonical_link: e.target.value })}
            />
            <Typography variant="body1" mt={2}>Custom Scripts</Typography>
            <CKEditorField
              value={editProcedureItem.custom_scripts}
              onChange={(data) => setEditProcedureItem({ ...editProcedureItem, custom_scripts: data })}
              uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
            />
            <TextField
              fullWidth
              margin="normal"
              label="OG Title"
              value={editProcedureItem.og_title}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, og_title: e.target.value })}
            />
            <Typography variant="body1" mt={2}>OG Description</Typography>
            <CKEditorField
              value={editProcedureItem.og_description}
              onChange={(data) => setEditProcedureItem({ ...editProcedureItem, og_description: data })}
              uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
            />
            <TextField
              fullWidth
              margin="normal"
              label="OG Type"
              value={editProcedureItem.og_type}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, og_type: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="OG URL"
              value={editProcedureItem.og_url}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, og_url: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Twitter Title"
              value={editProcedureItem.twitter_title}
              onChange={(e) => setEditProcedureItem({ ...editProcedureItem, twitter_title: e.target.value })}
            />
            <Typography variant="body1" mt={2}>Twitter Description</Typography>
            <CKEditorField
              value={editProcedureItem.twitter_description}
              onChange={(data) => setEditProcedureItem({ ...editProcedureItem, twitter_description: data })}
              uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
            />

            <Box mt={3}>
              {editProcedureItem.img_path && (
                <Box mb={1}>
                  <Typography variant="body2">Current Thumbnail:</Typography>
                  <img src={`${baseImage_path}/${editProcedureItem.img_path.replace(/^\.\//, "")}`} alt="thumb" style={{ width: '100px' }} />
                </Box>
              )}
              <Button variant="contained" component="label">
                Upload New Thumbnail
                <input type="file" accept="image/*" hidden onChange={(e) => setImgFile(e.target.files[0])} />
              </Button>
              <br /><Typography variant="caption" sx={{ color: 'red' }}>Procedure Image should be 60x60px</Typography>
            </Box>

            <Box mt={3}>
              {editProcedureItem.banner_img && (
                <Box mb={1}>
                  <Typography variant="body2">Current Banner:</Typography>
                  <img src={`${baseImage_path}/${editProcedureItem.banner_img.replace(/^\.\//, "")}`} alt="banner" style={{ width: '150px' }} />
                </Box>
              )}
              <Button variant="contained" component="label">
                Upload New Banner
                <input type="file" accept="image/*" hidden onChange={(e) => setBannerImgFile(e.target.files[0])} />
              </Button>
              <br /><Typography variant="caption" sx={{ color: 'red' }}>Banner Image should be 1380x260px</Typography>
            </Box>

            <Box mt={3}>
              {editProcedureItem.og_image && (
                <Box mb={1}>
                  <Typography variant="body2">Current OG Image:</Typography>
                  <img src={`${baseImage_path}/${editProcedureItem.og_image.replace(/^\.\//, "")}`} alt="og" style={{ width: '100px' }} />
                </Box>
              )}
              <Button variant="contained" component="label">
                Upload New OG Image
                <input type="file" accept="image/*" hidden onChange={(e) => setOgImgFile(e.target.files[0])} />
              </Button>
            </Box>

            <Box mt={3}>
              {editProcedureItem.twitter_image && (
                <Box mb={1}>
                  <Typography variant="body2">Current Twitter Image:</Typography>
                  <img src={`${baseImage_path}/${editProcedureItem.twitter_image.replace(/^\.\//, "")}`} alt="twitter" style={{ width: '100px' }} />
                </Box>
              )}
              <Button variant="contained" component="label">
                Upload New Twitter Image
                <input type="file" accept="image/*" hidden onChange={(e) => setTwitterImgFile(e.target.files[0])} />
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditProcedureItem(null)} color="secondary">Cancel</Button>
            <Button onClick={handleSaveEdit} color="primary" variant="contained" disabled={loadingAction}>
              {loadingAction ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteProcedureId} onClose={() => setDeleteProcedureId(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this procedure?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProcedureId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loadingAction}>
            {loadingAction ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcedureList;
